/**
 * Conduit Tool - BIM 2025 Grade
 * 
 * Conduit routing with:
 * - Multiple conduit types (EMT, RMC, PVC, ENT, IMC)
 * - Fill calculation per NEC 310
 * - Wire management
 * - Auto-fittings (elbows, couplings, bodies)
 * - 360° bend limit tracking
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Conduit Types ====================

export type ConduitType =
    | 'EMT'             // Electrical Metallic Tubing
    | 'RMC'             // Rigid Metal Conduit
    | 'IMC'             // Intermediate Metal Conduit
    | 'PVC'             // Rigid PVC
    | 'LFMC'            // Liquidtight Flexible Metal Conduit
    | 'ENT'             // Electrical Nonmetallic Tubing
    | 'FMC';            // Flexible Metal Conduit

// ==================== Wire Data ====================

export interface WireEntry {
    id: string;
    type: string;           // e.g., "THHN", "XHHW"
    size: string;           // e.g., "#12", "#10", "250 kcmil"
    areaWithInsulation: number; // mm² - Per NEC Table 5
    quantity: number;
}

// ==================== Conduit Sizes (Trade Sizes) ====================

export interface ConduitSize {
    tradeSize: string;      // e.g., "1/2", "3/4", "1"
    nominalDiameter: number; // mm
    innerDiameter: number;   // mm - Actual internal area
    area: number;            // mm² - Internal cross-sectional area
}

export const CONDUIT_SIZES: Record<ConduitType, ConduitSize[]> = {
    'EMT': [
        { tradeSize: '1/2"', nominalDiameter: 16, innerDiameter: 15.8, area: 196 },
        { tradeSize: '3/4"', nominalDiameter: 21, innerDiameter: 20.9, area: 343 },
        { tradeSize: '1"', nominalDiameter: 27, innerDiameter: 26.6, area: 556 },
        { tradeSize: '1-1/4"', nominalDiameter: 35, innerDiameter: 35.1, area: 968 },
        { tradeSize: '1-1/2"', nominalDiameter: 41, innerDiameter: 40.9, area: 1314 },
        { tradeSize: '2"', nominalDiameter: 53, innerDiameter: 52.5, area: 2165 },
        { tradeSize: '2-1/2"', nominalDiameter: 63, innerDiameter: 69.4, area: 3783 },
        { tradeSize: '3"', nominalDiameter: 78, innerDiameter: 78.5, area: 4840 },
        { tradeSize: '4"', nominalDiameter: 103, innerDiameter: 102.9, area: 8316 },
    ],
    'RMC': [
        { tradeSize: '1/2"', nominalDiameter: 21, innerDiameter: 16.1, area: 204 },
        { tradeSize: '3/4"', nominalDiameter: 27, innerDiameter: 21.2, area: 353 },
        { tradeSize: '1"', nominalDiameter: 33, innerDiameter: 27.0, area: 573 },
        { tradeSize: '1-1/4"', nominalDiameter: 42, innerDiameter: 35.4, area: 984 },
        { tradeSize: '1-1/2"', nominalDiameter: 48, innerDiameter: 41.2, area: 1333 },
        { tradeSize: '2"', nominalDiameter: 60, innerDiameter: 52.9, area: 2198 },
    ],
    'PVC': [
        { tradeSize: '1/2"', nominalDiameter: 21, innerDiameter: 15.5, area: 189 },
        { tradeSize: '3/4"', nominalDiameter: 27, innerDiameter: 20.4, area: 327 },
        { tradeSize: '1"', nominalDiameter: 33, innerDiameter: 26.1, area: 535 },
        { tradeSize: '1-1/4"', nominalDiameter: 42, innerDiameter: 34.5, area: 935 },
        { tradeSize: '1-1/2"', nominalDiameter: 48, innerDiameter: 40.4, area: 1282 },
        { tradeSize: '2"', nominalDiameter: 60, innerDiameter: 52.0, area: 2124 },
        { tradeSize: '2-1/2"', nominalDiameter: 73, innerDiameter: 62.7, area: 3088 },
        { tradeSize: '3"', nominalDiameter: 89, innerDiameter: 77.9, area: 4768 },
        { tradeSize: '4"', nominalDiameter: 114, innerDiameter: 101.5, area: 8091 },
    ],
    'IMC': [
        { tradeSize: '1/2"', nominalDiameter: 19, innerDiameter: 16.8, area: 222 },
        { tradeSize: '3/4"', nominalDiameter: 25, innerDiameter: 21.9, area: 377 },
        { tradeSize: '1"', nominalDiameter: 32, innerDiameter: 28.1, area: 620 },
    ],
    'LFMC': [
        { tradeSize: '1/2"', nominalDiameter: 16, innerDiameter: 12.5, area: 123 },
        { tradeSize: '3/4"', nominalDiameter: 21, innerDiameter: 16.1, area: 204 },
        { tradeSize: '1"', nominalDiameter: 27, innerDiameter: 21.1, area: 350 },
    ],
    'ENT': [
        { tradeSize: '1/2"', nominalDiameter: 16, innerDiameter: 14.2, area: 158 },
        { tradeSize: '3/4"', nominalDiameter: 21, innerDiameter: 18.3, area: 263 },
        { tradeSize: '1"', nominalDiameter: 27, innerDiameter: 24.3, area: 464 },
    ],
    'FMC': [
        { tradeSize: '1/2"', nominalDiameter: 16, innerDiameter: 12.7, area: 127 },
        { tradeSize: '3/4"', nominalDiameter: 21, innerDiameter: 16.0, area: 201 },
        { tradeSize: '1"', nominalDiameter: 27, innerDiameter: 21.0, area: 346 },
    ]
};

// NEC Fill percentages
export const FILL_PERCENTAGES = {
    oneWire: 53,        // 1 wire
    twoWires: 31,       // 2 wires
    threeOrMore: 40     // 3+ wires
};

// ==================== Fitting Types ====================

export type ConduitFittingType =
    | 'Elbow 90'
    | 'Elbow 45'
    | 'Elbow 30'
    | 'LB Body'
    | 'LL Body'
    | 'LR Body'
    | 'T Body'
    | 'C Body'
    | 'Coupling'
    | 'Connector'
    | 'Expansion Joint';

export interface ConduitFitting {
    id: string;
    type: ConduitFittingType;
    position: [number, number, number];
    angle: number;              // Degrees of bend
    connectedConduits: string[];
}

// ==================== Enhanced Geometry ====================

export interface EnhancedConduitGeometryData {
    // Route
    start: [number, number, number];
    end: [number, number, number];

    // Type and size
    conduitType: ConduitType;
    tradeSize: string;
    outerDiameter: number;      // mm
    innerDiameter: number;      // mm

    // Level
    referenceLevelId: string;
    offset: number;             // mm

    // Fittings
    fittings: ConduitFitting[];

    // Wires
    wires: WireEntry[];

    // Bend tracking
    totalBendAngle: number;     // Degrees (max 360° between pull points)

    // Calculated
    length: number;             // m
}

// ==================== Fill Calculation ====================

export interface ConduitFillCalculation {
    conduitArea: number;        // mm²
    wireArea: number;           // mm² - Total wire area
    fillPercentage: number;     // %
    maxFillAllowed: number;     // % - Based on wire count
    isCompliant: boolean;
    remainingCapacity: number;  // mm²
    wireCount: number;
}

// ==================== Enhanced Conduit Element ====================

export interface EnhancedConduitElement extends Element {
    category: 'Conduits';
    geometryData: EnhancedConduitGeometryData;

    // Fill calculation
    fillCalculation: ConduitFillCalculation;

    // Connectors
    connectors: {
        start: { id: string; position: [number, number, number]; connectedTo?: string };
        end: { id: string; position: [number, number, number]; connectedTo?: string };
    };
}

// ==================== Tool Options ====================

export interface ConduitToolOptions {
    // Type and size
    conduitType: ConduitType;
    tradeSize: string;

    // Level
    referenceLevelId: string;
    offset: number;

    // Routing
    chainMode: boolean;
    autoFittings: boolean;

    // Bend tracking
    trackBends: boolean;
    warnAt360: boolean;
}

// ==================== Conduit Tool Class ====================

export class ConduitTool extends BaseTool {
    id = 'conduit';
    name = 'Conduit';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    private lastCreatedConduit: EnhancedConduitElement | null = null;
    private currentRunBendAngle: number = 0;

    private options: ConduitToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): ConduitToolOptions {
        return {
            conduitType: 'EMT',
            tradeSize: '3/4"',
            referenceLevelId: 'Level 1',
            offset: 3000,
            chainMode: true,
            autoFittings: true,
            trackBends: true,
            warnAt360: true
        };
    }

    /**
     * Set conduit options from UI
     */
    setOptions(options: Partial<ConduitToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): ConduitToolOptions {
        return { ...this.options };
    }

    /**
     * Get available sizes for conduit type
     */
    getAvailableSizes(): ConduitSize[] {
        return CONDUIT_SIZES[this.options.conduitType] || [];
    }

    /**
     * Get current size data
     */
    getCurrentSize(): ConduitSize | undefined {
        const sizes = this.getAvailableSizes();
        return sizes.find(s => s.tradeSize === this.options.tradeSize);
    }

    /**
     * Calculate fill for given wires
     */
    calculateFill(wires: WireEntry[]): ConduitFillCalculation {
        const size = this.getCurrentSize();
        if (!size) {
            return {
                conduitArea: 0,
                wireArea: 0,
                fillPercentage: 0,
                maxFillAllowed: 0,
                isCompliant: false,
                remainingCapacity: 0,
                wireCount: 0
            };
        }

        let wireArea = 0;
        let wireCount = 0;
        for (const wire of wires) {
            wireArea += wire.areaWithInsulation * wire.quantity;
            wireCount += wire.quantity;
        }

        // Determine max fill based on wire count
        let maxFill: number;
        if (wireCount === 1) maxFill = FILL_PERCENTAGES.oneWire;
        else if (wireCount === 2) maxFill = FILL_PERCENTAGES.twoWires;
        else maxFill = FILL_PERCENTAGES.threeOrMore;

        const fillPercentage = (wireArea / size.area) * 100;

        return {
            conduitArea: size.area,
            wireArea,
            fillPercentage: Math.round(fillPercentage * 10) / 10,
            maxFillAllowed: maxFill,
            isCompliant: fillPercentage <= maxFill,
            remainingCapacity: Math.max(0, (maxFill / 100) * size.area - wireArea),
            wireCount
        };
    }

    /**
     * Recommend conduit size based on wires
     */
    recommendSize(wires: WireEntry[]): string {
        let totalArea = 0;
        let wireCount = 0;
        for (const wire of wires) {
            totalArea += wire.areaWithInsulation * wire.quantity;
            wireCount += wire.quantity;
        }

        let maxFill: number;
        if (wireCount === 1) maxFill = FILL_PERCENTAGES.oneWire;
        else if (wireCount === 2) maxFill = FILL_PERCENTAGES.twoWires;
        else maxFill = FILL_PERCENTAGES.threeOrMore;

        const requiredArea = totalArea / (maxFill / 100);

        const sizes = this.getAvailableSizes();
        for (const size of sizes) {
            if (size.area >= requiredArea) {
                return size.tradeSize;
            }
        }

        return sizes[sizes.length - 1].tradeSize;
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            if (snapped.distanceTo(this.startPoint) < 0.05) return;

            const conduit = this.createConduit(this.startPoint, snapped);
            this.context.bimStore.addElement(conduit);

            // Track bends and create fitting
            if (this.lastCreatedConduit && this.options.autoFittings) {
                const bendAngle = this.createFitting(this.lastCreatedConduit, conduit, this.startPoint);
                if (this.options.trackBends) {
                    this.currentRunBendAngle += bendAngle;
                    if (this.options.warnAt360 && this.currentRunBendAngle >= 360) {
                        // Would need to notify user through UI
                        conduit.parameters['Bend Warning'] = 'Exceeds 360° - Pull point required';
                    }
                }
            }

            this.lastCreatedConduit = conduit;

            if (this.options.chainMode) {
                this.startPoint = snapped;
            } else {
                this.startPoint = null;
                this.currentPoint = null;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.lastCreatedConduit = null;
                this.currentRunBendAngle = 0;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        } else if (event.key === 'Enter') {
            // Finish run, reset bend tracking
            this.startPoint = null;
            this.currentPoint = null;
            this.lastCreatedConduit = null;
            this.currentRunBendAngle = 0;
            this.context?.appStore.setToolPreview(null);
        }
    }

    private updatePreview(): void {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            const size = this.getCurrentSize();
            this.context.appStore.setToolPreview({
                type: 'conduit',
                start: this.startPoint,
                end: this.currentPoint,
                conduitType: this.options.conduitType,
                diameter: (size?.nominalDiameter || 25) / 1000,
                currentBendAngle: this.currentRunBendAngle
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createConduit(start: THREE.Vector3, end: THREE.Vector3): EnhancedConduitElement {
        const size = this.getCurrentSize();
        if (!size) throw new Error('Invalid conduit size');

        const length = start.distanceTo(end);
        const fillCalc = this.calculateFill([]);

        const geometryData: EnhancedConduitGeometryData = {
            start: start.toArray() as [number, number, number],
            end: end.toArray() as [number, number, number],
            conduitType: this.options.conduitType,
            tradeSize: this.options.tradeSize,
            outerDiameter: size.nominalDiameter,
            innerDiameter: size.innerDiameter,
            referenceLevelId: this.options.referenceLevelId,
            offset: this.options.offset,
            fittings: [],
            wires: [],
            totalBendAngle: this.currentRunBendAngle,
            length
        };

        const conduit: EnhancedConduitElement = {
            id: uuidv4(),
            category: 'Conduits',
            type: `${this.options.conduitType}: ${this.options.tradeSize}`,
            parameters: this.buildParameters(geometryData, fillCalc),
            constraints: this.getDefaultConstraints(),
            geometryData,
            fillCalculation: fillCalc,
            connectors: {
                start: { id: uuidv4(), position: start.toArray() as [number, number, number] },
                end: { id: uuidv4(), position: end.toArray() as [number, number, number] }
            }
        };

        return conduit;
    }

    private createFitting(conduit1: EnhancedConduitElement, conduit2: EnhancedConduitElement, point: THREE.Vector3): number {
        const dir1 = new THREE.Vector3(
            conduit1.geometryData.end[0] - conduit1.geometryData.start[0],
            conduit1.geometryData.end[1] - conduit1.geometryData.start[1],
            conduit1.geometryData.end[2] - conduit1.geometryData.start[2]
        ).normalize();

        const dir2 = new THREE.Vector3(
            conduit2.geometryData.end[0] - conduit2.geometryData.start[0],
            conduit2.geometryData.end[1] - conduit2.geometryData.start[1],
            conduit2.geometryData.end[2] - conduit2.geometryData.start[2]
        ).normalize();

        const angle = Math.acos(Math.abs(dir1.dot(dir2))) * (180 / Math.PI);

        if (angle > 5) {
            let fittingType: ConduitFittingType;
            if (Math.abs(angle - 90) < 10) fittingType = 'Elbow 90';
            else if (Math.abs(angle - 45) < 10) fittingType = 'Elbow 45';
            else if (Math.abs(angle - 30) < 10) fittingType = 'Elbow 30';
            else fittingType = 'Elbow 90'; // Default

            const fitting: ConduitFitting = {
                id: uuidv4(),
                type: fittingType,
                position: point.toArray() as [number, number, number],
                angle,
                connectedConduits: [conduit1.id, conduit2.id]
            };

            conduit1.geometryData.fittings.push(fitting);
            return angle;
        }

        return 0;
    }

    private buildParameters(geom: EnhancedConduitGeometryData, fill: ConduitFillCalculation): Record<string, any> {
        return {
            // Type and size
            'Conduit Type': geom.conduitType,
            'Trade Size': geom.tradeSize,
            'Outer Diameter': geom.outerDiameter,
            'Inner Diameter': geom.innerDiameter,

            // Length
            'Length': Math.round(geom.length * 1000) / 1000,

            // Level
            'Reference Level': geom.referenceLevelId,
            'Offset': geom.offset,

            // Fill
            'Wire Count': fill.wireCount,
            'Fill Percentage': fill.fillPercentage,
            'Max Fill Allowed': fill.maxFillAllowed,
            'Fill Compliant': fill.isCompliant,

            // Bends
            'Total Bend Angle': geom.totalBendAngle,
            'Bend Count': geom.fittings.filter(f => f.type.includes('Elbow')).length
        };
    }

    private getDefaultConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.referenceLevelId,
                data: { offset: this.options.offset }
            }
        ];
    }
}

// ==================== Helper Functions ====================

/**
 * Add wire to conduit and recalculate fill
 */
export function addWireToConduit(conduit: EnhancedConduitElement, wire: WireEntry): boolean {
    const newWires = [...conduit.geometryData.wires, wire];

    let wireArea = 0;
    let wireCount = 0;
    for (const w of newWires) {
        wireArea += w.areaWithInsulation * w.quantity;
        wireCount += w.quantity;
    }

    let maxFill: number;
    if (wireCount === 1) maxFill = FILL_PERCENTAGES.oneWire;
    else if (wireCount === 2) maxFill = FILL_PERCENTAGES.twoWires;
    else maxFill = FILL_PERCENTAGES.threeOrMore;

    const fillPercentage = (wireArea / conduit.fillCalculation.conduitArea) * 100;

    if (fillPercentage > maxFill) {
        return false;
    }

    conduit.geometryData.wires.push(wire);
    conduit.fillCalculation.wireArea = wireArea;
    conduit.fillCalculation.wireCount = wireCount;
    conduit.fillCalculation.fillPercentage = Math.round(fillPercentage * 10) / 10;
    conduit.fillCalculation.maxFillAllowed = maxFill;
    conduit.fillCalculation.isCompliant = true;
    conduit.fillCalculation.remainingCapacity =
        (maxFill / 100) * conduit.fillCalculation.conduitArea - wireArea;

    return true;
}

